import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Descriptions, Empty, Segmented, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import InfoAyuda, { AyudaBloque } from "../../../Components/InfoAyuda";
import { useFiltroPersistido } from "../../../hooks/useFiltroPersistido";
import { botAsistenteApi, FRASE_VACIAR_SIMULACIONES } from "../../../services/botAsistente.api";
import type { BotEstado, BotSimulacion, BotSimulacionEstado } from "../../../interfaces/botAsistente.interfaces";
import {
  acortar,
  cantidadRegistrosSimulacion,
  formatoChile,
  formatoDia,
  formatoMonto,
  gastosDeSimulacion,
  pluralizar,
} from "./botAsistente.utils";
import ConfirmacionEscrita from "./ConfirmacionEscrita";
import { expansorFila } from "./ExpansorFila";
import { crearLectorSinSolapes } from "./useRelecturaPeriodica";
import "./tablasPanel.css";

const { Text } = Typography;

type FiltroEstado = "todas" | BotSimulacionEstado;
const FILTROS: readonly FiltroEstado[] = ["todas", "simulada", "deshecha"];

// Cada motivo dice QUÉ falló de verdad: la lista, el resumen de conteos o los dos (antes, si solo fallaba el resumen, decía que no se
// había podido leer la lista, que sí había cargado).
const MOTIVO_SIN_LISTA = "No se pudo leer la lista de simulaciones. Reintenta antes de vaciar.";
const MOTIVO_SIN_CONTEOS = "No se pudo leer el resumen de conteos de simulaciones. Toca «Actualizar» antes de vaciar.";
const MOTIVO_SIN_LISTA_NI_CONTEOS = "No se pudieron leer la lista de simulaciones ni el resumen de conteos. Reintenta antes de vaciar.";
const MOTIVO_CARGANDO = "Cargando la lista…";
const MOTIVO_NADA = "No hay simulaciones para vaciar.";

export interface SimulacionesSeccionProps {
  estado: BotEstado | null;
  errorEstado: boolean;
  /** Cambia cuando algo externo (por ejemplo el modo) obliga a volver a leer la lista. */
  version: number;
  /** Se llama después de vaciar: el contenedor vuelve a leer los conteos. */
  onVaciado: () => void | Promise<void>;
  /** «Actualizar» también vuelve a leer los conteos (si no, «Total para vaciar» podía quedar atrás de la lista). */
  onActualizarConteos?: () => void;
  /**
   * Sube cada vez que el contenedor relee los conteos en segundo plano (cada 45 s): la lista vuelve a leer SOLO la página que se
   * está viendo, en silencio (sin spinner, sin error si falla y sin cerrar una fila abierta), para que «Total para vaciar: 5» no
   * quede junto a 3 filas. 0 o ausente = nunca se relee sola.
   */
  relectura?: number;
}

/** Quién escribió: su nombre, o «Usuario #id» si el servidor no lo trae. Texto del servidor: siempre como texto. */
const quienEscribio = (s: BotSimulacion): string => (s.actorNombre ? acortar(s.actorNombre, 60) : `Usuario #${s.actorUserId}`);

/** Lo que va en la fila principal: el monto que habría guardado (suma de sus gastos) y, debajo, de qué se trata. */
const ResumenGuardado: React.FC<{ s: BotSimulacion }> = ({ s }) => {
  const gastos = gastosDeSimulacion(s);
  if (gastos.length === 0) {
    const n = cantidadRegistrosSimulacion(s);
    return <Text type="secondary">{n > 0 ? `${n} ${pluralizar(n, "registro", "registros")} (sin detalle)` : "Sin detalle"}</Text>;
  }
  const total = gastos.reduce((suma, g) => suma + g.monto, 0);
  return (
    <div style={{ wordBreak: "break-word" }}>
      <Text strong>{formatoMonto(total)}</Text>
      {gastos.length > 1 && <Text type="secondary"> · {gastos.length} gastos</Text>}
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {acortar(String(gastos[0].descripcion ?? ""), 60)}
        </Text>
      </div>
    </div>
  );
};

/** «Simulada» o «Deshecha» (con la hora en que se deshizo, en hora de Chile). */
const EstadoDeLaSimulacion: React.FC<{ s: BotSimulacion }> = ({ s }) =>
  s.estado === "deshecha" ? (
    <Space direction="vertical" size={0}>
      <Tag style={{ margin: 0 }}>Deshecha</Tag>
      {s.deshechaEn && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatoChile(s.deshechaEn)}
        </Text>
      )}
    </Space>
  ) : (
    <Tag color="blue" style={{ margin: 0 }}>
      Simulada
    </Tag>
  );

/** Lo que se ve al abrir la fila: cada gasto con su descripción, categoría y día, tal como los calculó el servidor. */
const DetalleSimulacion: React.FC<{ s: BotSimulacion }> = ({ s }) => {
  const gastos = gastosDeSimulacion(s);
  return (
    <Descriptions size="small" bordered column={1} data-testid={`simulacion-detalle-${s.id}`}>
      <Descriptions.Item label="Lo que habría guardado">
        {gastos.length === 0 ? (
          <Text type="secondary">Sin detalle</Text>
        ) : (
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            {gastos.map((g, i) => {
              const detalle = [g.categoria, formatoDia(g.fecha)].filter((x): x is string => typeof x === "string" && x !== "");
              return (
                // Texto del servidor: siempre como texto (React lo escapa), recortado si es larguísimo.
                <div key={i} style={{ wordBreak: "break-word" }}>
                  <Text strong>{formatoMonto(g.monto)}</Text> <Text>{acortar(String(g.descripcion ?? ""), 160)}</Text>
                  {detalle.length > 0 && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {acortar(detalle.join(" · "), 120)}
                      </Text>
                    </div>
                  )}
                </div>
              );
            })}
          </Space>
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Operación">{acortar(String(s.operacionId ?? ""), 40) || "—"}</Descriptions.Item>
      {s.estado === "deshecha" && <Descriptions.Item label="Deshecha el">{formatoChile(s.deshechaEn)}</Descriptions.Item>}
    </Descriptions>
  );
};

/**
 * (d) Simulaciones. Lo que el asistente HABRÍA guardado mientras el modo real está apagado, con los conteos y el botón
 * «Vaciar simulaciones» (confirmación con la frase VACIAR SIMULACIONES). Vaciar borra solo esta lista: nunca toca un
 * gasto real; el servidor lo garantiza y el texto de la pantalla lo dice.
 *
 * La fila principal muestra lo que importa sin deslizar, también a 360 px: fecha y quién (apilados), el monto y el estado.
 * El resto (cada gasto con su descripción, categoría y día) está al abrir la fila. Mientras carga, o si no se pudo leer,
 * NO se afirma que «no hay simulaciones» y «Vaciar» queda apagado diciendo por qué.
 */
const SimulacionesSeccion: React.FC<SimulacionesSeccionProps> = ({ estado, errorEstado, version, onVaciado, onActualizarConteos, relectura = 0 }) => {
  const [filtro, setFiltro] = useFiltroPersistido<FiltroEstado>("asistenteWhatsapp.simulacionesEstado", "todas", {
    validar: (v) => FILTROS.some((f) => f === v),
  });
  const [filas, setFilas] = useState<BotSimulacion[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [tamano, setTamano] = useState(10);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const secuencia = useRef(0);
  /** Hay una lectura PEDIDA POR LA PERSONA en curso (abrir, filtrar, paginar, «Actualizar»): la relectura de fondo no se mete en medio. */
  const leyendo = useRef(false);
  /** Guarda de la relectura de fondo: no lanza otra mientras la anterior sigue en vuelo (con el servidor colgado se acumularían). */
  const [lectorDeFondo] = useState(() => crearLectorSinSolapes());

  const [confirmando, setConfirmando] = useState(false);
  const [vaciando, setVaciando] = useState(false);
  const [resultado, setResultado] = useState<number | null>(null);

  /** `silenciosa`: relectura de fondo. No enciende el spinner y, si falla, deja la lista que ya se ve (no la reemplaza por un error). */
  const cargar = useCallback(
    async function leer(p: number, s: number, silenciosa = false): Promise<void> {
      // Una lectura de fondo no pisa a una que la persona pidió: esa ya trae lo último y, si se cruzaran, la de la persona se descartaría.
      if (silenciosa && leyendo.current) return;
      const mia = ++secuencia.current;
      if (!silenciosa) {
        leyendo.current = true;
        setCargando(true);
      }
      const res = await botAsistenteApi.listarSimulaciones({
        page: p,
        size: s,
        estado: filtro === "todas" ? undefined : filtro,
      });
      if (mia !== secuencia.current) return; // llegó tarde: ya se pidió otra cosa
      if (res === null) {
        if (silenciosa) return;
        setError(true);
      } else {
        // Si alguien vació o borró mientras se miraba, la página puede haber dejado de existir: se vuelve a la última que sí.
        const ultima = Math.max(1, Math.ceil(res.total / s));
        if (silenciosa && res.data.length === 0 && p > ultima) return leer(ultima, s, true);
        setError(false);
        setFilas(res.data);
        setTotal(res.total);
        setPagina(res.page);
        setTamano(res.size);
      }
      leyendo.current = false;
      setCargando(false);
    },
    [filtro],
  );

  // Se vuelve a leer desde la página 1 al cambiar el filtro o la versión; un cambio de página usa onChange de la tabla.
  useEffect(() => {
    void cargar(1, tamano);
    // `tamano` no se incluye a propósito: cambiar el tamaño de página ya llama a `cargar` desde la tabla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargar, version]);

  // Relectura de fondo (la dispara el contenedor junto con la de los conteos). Solo la página visible; no corre al montar.
  useEffect(() => {
    if (relectura === 0) return;
    lectorDeFondo("lista", () => cargar(pagina, tamano, true));
    // Solo cuando sube el contador: pagina, tamano y cargar son los de ESTE render (los vigentes) y no deben disparar otra lectura.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relectura]);

  const vaciar = async () => {
    if (vaciando) return;
    setVaciando(true);
    try {
      // Se manda la frase canónica: lo que la persona escribió solo fue la compuerta (ConfirmacionEscrita).
      const res = await botAsistenteApi.vaciarSimulaciones(FRASE_VACIAR_SIMULACIONES);
      setConfirmando(false);
      if (res !== null) {
        setResultado(typeof res.eliminadas === "number" ? res.eliminadas : 0);
        await cargar(1, tamano);
        await onVaciado();
      }
    } finally {
      setVaciando(false);
    }
  };

  const actualizar = () => {
    void cargar(pagina, tamano);
    onActualizarConteos?.();
  };

  // ¿Se puede vaciar? Solo si se pudo LEER la lista y hay algo. Los conteos del servidor y la lista se miran juntos: si una
  // simulación llegó después de leer los conteos, la lista ya la muestra y no se bloquea el botón por un número viejo.
  const hayAlgo = (estado?.simulacionesPendientes ?? 0) > 0 || total > 0;
  const motivoNoVaciar: string | null =
    error && errorEstado
      ? MOTIVO_SIN_LISTA_NI_CONTEOS
      : error
        ? MOTIVO_SIN_LISTA
        : errorEstado
          ? MOTIVO_SIN_CONTEOS
          : estado === null || (cargando && filas.length === 0)
        ? MOTIVO_CARGANDO
        : !hayAlgo
          ? MOTIVO_NADA
          : null;

  const columnas: ColumnsType<BotSimulacion> = [
    {
      title: "Fecha y quién",
      key: "cuando",
      render: (_: unknown, s) => (
        <div style={{ wordBreak: "break-word" }}>
          {/* La fecha en UNA línea (partida en tres, «30-10- / 2026 / 11:10», era ilegible); el nombre sí se parte. */}
          <div style={{ whiteSpace: "nowrap" }}>{formatoChile(s.creadoEn)}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {quienEscribio(s)}
          </Text>
        </div>
      ),
    },
    {
      // Monto y estado juntos: con tres columnas (expandir, fecha y quién, monto y estado) la fila principal cabe en 360 px
      // sin deslizar; con el estado en una cuarta columna quedaba 100 px más ancha que la pantalla.
      title: "Habría guardado y estado",
      key: "gastos",
      render: (_: unknown, s) => (
        <div>
          <ResumenGuardado s={s} />
          <div style={{ marginTop: 4 }}>
            <EstadoDeLaSimulacion s={s} />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Card
      size="small"
      data-testid="asistente-seccion-simulaciones"
      title={
        <Space size={4}>
          <span>Simulaciones</span>
          <InfoAyuda titulo="Simulaciones" testId="ayuda-simulaciones">
            <AyudaBloque titulo="¿Qué son?">
              Con el modo real apagado, cada gasto que entra por WhatsApp se revisa igual que uno real pero no se guarda.
              Aquí queda anotado lo que HABRÍA guardado, con la marca de simulación.
            </AyudaBloque>
            <AyudaBloque titulo="¿Qué hace «Vaciar simulaciones»?">
              Borra solo esta lista. No toca ningún gasto real ni ninguna otra tabla. Hay que escribir una frase para
              confirmar.
            </AyudaBloque>
            <AyudaBloque titulo="«Deshecha»">
              La persona que escribió por WhatsApp pidió deshacer esa simulación.
            </AyudaBloque>
            <AyudaBloque titulo="Ver el detalle">
              Toca una fila para ver cada gasto con su descripción, categoría y día.
            </AyudaBloque>
          </InfoAyuda>
        </Space>
      }
      extra={
        <Button size="small" icon={<ReloadOutlined />} onClick={actualizar} loading={cargando} data-testid="simulaciones-actualizar">
          Actualizar
        </Button>
      }
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="simulaciones-conteos">
          {errorEstado ? (
            <Text type="secondary">No se pudieron leer los conteos.</Text>
          ) : estado ? (
            <>
              <Tag color="blue" style={{ margin: 0 }}>
                Simuladas: {estado.simulacionesVigentes}
              </Tag>
              <Tag style={{ margin: 0 }}>Deshechas: {estado.simulacionesDeshechas}</Tag>
              <Tag style={{ margin: 0 }}>Total para vaciar: {estado.simulacionesPendientes}</Tag>
            </>
          ) : null}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
          <Segmented
            value={filtro}
            onChange={(v) => setFiltro(v as FiltroEstado)}
            options={[
              { label: "Todas", value: "todas" },
              { label: "Simuladas", value: "simulada" },
              { label: "Deshechas", value: "deshecha" },
            ]}
            data-testid="simulaciones-filtro"
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            disabled={motivoNoVaciar !== null}
            onClick={() => setConfirmando(true)}
            data-testid="simulaciones-vaciar"
          >
            Vaciar simulaciones
          </Button>
        </div>
        {motivoNoVaciar !== null && (
          <Text type="secondary" style={{ fontSize: 12 }} data-testid="simulaciones-vaciar-motivo">
            {motivoNoVaciar}
          </Text>
        )}

        {resultado !== null && (
          <Alert
            type="success"
            showIcon
            closable
            onClose={() => setResultado(null)}
            message={resultado === 1 ? "Se borró 1 simulación" : `Se borraron ${resultado} simulaciones`}
            description="Los gastos reales no se tocaron."
            data-testid="simulaciones-resultado"
          />
        )}

        {error ? (
          // No se pudo leer: se dice eso y se ofrece reintentar. NO se dibuja la tabla, porque su texto de vacío («no hay
          // simulaciones») afirmaría algo que no se sabe.
          <Alert
            type="error"
            showIcon
            data-testid="simulaciones-error"
            message="No se pudo cargar la lista de simulaciones"
            action={
              <Button size="small" icon={<ReloadOutlined />} onClick={() => void cargar(pagina, tamano)}>
                Reintentar
              </Button>
            }
          />
        ) : (
          <Table<BotSimulacion>
            // `tabla-asistente` (tablasPanel.css): sin el ancho mínimo de 800 px de las tablas del sistema, para que a 360 px
            // se vean la fecha, quién, el monto y el estado sin deslizar.
            className="tabla-asistente"
            rowKey="id"
            size="small"
            loading={cargando}
            dataSource={filas}
            columns={columnas}
            // La fila entera abre el detalle (expandRowByClick): el cursor de mano lo anuncia a quien tiene mouse.
            onRow={() => ({ style: { cursor: "pointer" } })}
            expandable={{
              expandRowByClick: true,
              expandIcon: expansorFila,
              columnWidth: 48,
              expandedRowRender: (s) => <DetalleSimulacion s={s} />,
            }}
            locale={{
              // Mientras carga no se dice nada: el texto de vacío se veía tenue debajo del indicador de carga.
              emptyText: cargando ? (
                <span />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No hay simulaciones. Aparecen aquí cuando alguien le escribe un gasto al asistente."
                />
              ),
            }}
            pagination={{
              current: pagina,
              pageSize: tamano,
              total,
              showSizeChanger: true,
              pageSizeOptions: [10, 25, 50],
              showTotal: (t) => `${t} ${pluralizar(t, "simulación", "simulaciones")}`,
              // Los DOS argumentos: ignorar el tamaño dejaría muerto el selector de «por página».
              onChange: (p, s) => void cargar(p, s),
            }}
          />
        )}
      </Space>

      <ConfirmacionEscrita
        abierto={confirmando}
        titulo="Vaciar simulaciones"
        frase={FRASE_VACIAR_SIMULACIONES}
        textoConfirmar="Vaciar simulaciones"
        cargando={vaciando}
        onConfirmar={vaciar}
        onCancelar={() => setConfirmando(false)}
        testId="confirmar-vaciar"
      >
        <p style={{ marginTop: 0 }}>
          Se borrarán <b>todas</b> las simulaciones de la lista. Es solo esta lista: <b>no se toca ningún gasto real</b> ni
          ninguna otra tabla.
        </p>
        <p style={{ marginBottom: 0 }}>Esto no se puede deshacer.</p>
      </ConfirmacionEscrita>
    </Card>
  );
};

export default SimulacionesSeccion;
